// Busca de pílulas e questões para adicionar como etapa no fim do curso.
import React, { useMemo, useState } from 'react';
import { FiBarChart2, FiBookOpen, FiCheckSquare, FiImage, FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { mensagensDaPilula } from './mensagensBot';
import { TIPOS_QUESTAO, paraBusca, plural } from './util';

const LIMITE = 40;

const FILTROS_QUESTAO = [
  { id: 'todas', rotulo: 'Todos os tipos' },
  { id: 'quiz', rotulo: 'Quiz (vale nota)' },
  { id: 'Fácil', rotulo: 'Quiz fácil' },
  { id: 'Médio', rotulo: 'Quiz médio' },
  { id: 'Difícil', rotulo: 'Quiz difícil' },
  { id: 'pesquisa', rotulo: 'Pesquisas (não valem nota)' },
];

function passaFiltroQuestao(q, filtro) {
  if (filtro === 'todas') return true;
  if (filtro === 'pesquisa') return String(q.type || '').startsWith('pesquisa');
  if (q.type !== 'quiz') return false;
  return filtro === 'quiz' || paraBusca(q.difficulty) === paraBusca(filtro);
}

export const IconeTipo = ({ etapa, tipo, questao }) => {
  const t = tipo || etapa?.tipoConteudo;
  const q = questao || etapa?.conteudo;
  if (t === 'pilula') return <FiBookOpen aria-hidden="true" />;
  if (q && String(q.type || '').startsWith('pesquisa')) return <FiBarChart2 aria-hidden="true" />;
  return <FiCheckSquare aria-hidden="true" />;
};

function AdicionarEtapa({ pilulas, questoes, temas, temaCursoId, idsNoCurso, proximaPosicao, onAdicionar, onFechar }) {
  const [tipo, setTipo] = useState('pilula');
  const [busca, setBusca] = useState('');
  const [tema, setTema] = useState(temaCursoId || '');
  const [filtroQuestao, setFiltroQuestao] = useState('todas');
  const [adicionando, setAdicionando] = useState(null);

  const resultados = useMemo(() => {
    const termo = paraBusca(busca.trim());
    // conteúdo sem tema (pesquisas, por exemplo) aparece com qualquer filtro de tema
    const doTema = (item) => !tema || !Array.isArray(item.temas) || !item.temas.length || item.temas.includes(tema);
    const lista = tipo === 'pilula'
      ? pilulas
        .filter((p) => !p.bloqueado && !idsNoCurso.has(p.id) && doTema(p))
        .filter((p) => !termo || paraBusca(`${p.titulo} ${p.conteudo}`).includes(termo))
        .map((p) => ({ item: p, titulo: p.titulo || '(sem título)', meta: 'Pílula', imagem: Boolean(mensagensDaPilula(p)[0].imagem) }))
      : questoes
        .filter((q) => !idsNoCurso.has(q.id) && doTema(q) && passaFiltroQuestao(q, filtroQuestao))
        .filter((q) => !termo || paraBusca(q.enunciado).includes(termo))
        .map((q) => ({
          item: q,
          titulo: q.enunciado || '(sem enunciado)',
          meta: q.type === 'quiz' ? `Quiz${q.difficulty ? ` · ${q.difficulty}` : ''}` : (TIPOS_QUESTAO[q.type] || 'Questão'),
        }));
    return lista.sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
  }, [tipo, busca, tema, filtroQuestao, pilulas, questoes, idsNoCurso]);

  const adicionar = async (r) => {
    setAdicionando(r.item.id);
    try {
      await onAdicionar(tipo, r.item);
    } finally {
      setAdicionando(null);
    }
  };

  const nomeTema = temas.find((t) => t.id === tema)?.nome;

  return (
    <section className="mc-adicionar" aria-labelledby="mc-adicionar-titulo">
      <header className="mc-adicionar__topo">
        <h3 id="mc-adicionar-titulo" className="mc-subtitulo">Adicionar etapa {proximaPosicao} (no fim)</h3>
        <button type="button" className="btn-ghost mc-icone-botao" onClick={onFechar} aria-label="Fechar busca de conteúdo">
          <FiX aria-hidden="true" />
        </button>
      </header>

      <div className="mc-segmentos" role="group" aria-label="Tipo de conteúdo">
        {[['pilula', 'Pílula'], ['questao', 'Questão']].map(([id, rotulo]) => (
          <button
            key={id}
            type="button"
            className={`mc-segmento${tipo === id ? ' is-ativo' : ''}`}
            aria-pressed={tipo === id}
            onClick={() => setTipo(id)}
          >
            <IconeTipo tipo={id} /> {rotulo}
          </button>
        ))}
      </div>

      <div className="mc-busca-linha">
        <div className="mc-busca">
          <FiSearch aria-hidden="true" />
          <label htmlFor="mc-busca-conteudo" className="mc-oculto">
            {tipo === 'pilula' ? 'Buscar pílula pelo título ou texto' : 'Buscar questão pelo enunciado'}
          </label>
          <input
            id="mc-busca-conteudo"
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder={tipo === 'pilula' ? 'Buscar pílula...' : 'Buscar questão...'}
            autoComplete="off"
          />
        </div>
        <label htmlFor="mc-busca-tema" className="mc-oculto">Tema</label>
        <select id="mc-busca-tema" className="mc-select" value={tema} onChange={(e) => setTema(e.target.value)}>
          <option value="">Todos os temas</option>
          {temas.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
        {tipo === 'questao' && (
          <>
            <label htmlFor="mc-busca-tipo" className="mc-oculto">Tipo de questão</label>
            <select id="mc-busca-tipo" className="mc-select" value={filtroQuestao} onChange={(e) => setFiltroQuestao(e.target.value)}>
              {FILTROS_QUESTAO.map((f) => <option key={f.id} value={f.id}>{f.rotulo}</option>)}
            </select>
          </>
        )}
      </div>

      <p className="mc-suave" role="status">
        {resultados.length
          ? `${plural(resultados.length, tipo === 'pilula' ? 'pílula disponível' : 'questão disponível', tipo === 'pilula' ? 'pílulas disponíveis' : 'questões disponíveis')}${nomeTema ? ` em ${nomeTema}` : ''}${resultados.length > LIMITE ? `; mostrando ${LIMITE}, refine a busca` : ''}.`
          : `Nada encontrado${nomeTema ? ` em ${nomeTema}` : ''}. ${tema ? 'Tente "Todos os temas". ' : ''}O que já está no curso não aparece aqui.`}
      </p>

      {resultados.length > 0 && (
        <ul className="mc-resultados">
          {resultados.slice(0, LIMITE).map((r) => (
            <li key={r.item.id} className="mc-resultado">
              <span className="mc-resultado__icone"><IconeTipo tipo={tipo} questao={r.item} /></span>
              <span className="mc-resultado__texto">
                <span className="mc-resultado__titulo">{r.titulo}</span>
                <span className="mc-resultado__meta">
                  {r.meta}
                  {r.imagem && <span className="mc-chip"><FiImage aria-hidden="true" /> imagem</span>}
                </span>
              </span>
              <button
                type="button"
                className="btn-secondary mc-botao mc-botao--pequeno"
                onClick={() => adicionar(r)}
                disabled={Boolean(adicionando)}
                aria-label={`Adicionar como etapa ${proximaPosicao}: ${r.titulo}`}
              >
                <FiPlus aria-hidden="true" /> {adicionando === r.item.id ? 'Adicionando...' : 'Adicionar'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default AdicionarEtapa;
